import { z } from 'zod';
// Meta engagement-digest schemas (phase 2 of the meta-cm roadmap).
//
// Daily-ish scan that reads per-post insights + account-level
// audience signal and produces a digest card: best time to post,
// overperformer + hypothesis, underperformer + hypothesis, and a
// boost recommendation (clickable once paid Meta lands).
//
// Scopes required (added to OAuth in s216-p2):
//   - instagram_manage_insights — per-post reach/impressions/saves
//     + account-level online_followers_by_hour.
//
// What lives here:
//   - postInsightsSchema — what we keep after a /{media}/insights
//     fetch (trimmed from Graph's verbose response).
//   - audienceCurveSchema — the online-followers-by-hour curve from
//     /{ig-user-id}/insights?metric=online_followers&period=lifetime.
//   - metaEngagementDigestAgent[Input|Output]Schema — agent contract.
//   - metaEngagementDigestCardPayloadSchema — feed_card payload.
// ─── Per-post insights ───────────────────────────────────────────────
/** Trimmed per-post insights from the Graph API
 *  `/{media-id}/insights?metric=reach,impressions,saved,total_interactions,video_views`
 *  call. Each metric is nullable because the available set varies
 *  by media_type (reels expose video_views; static photos don't). */
export const postInsightsSchema = z.object({
    /** IG media id this snapshot belongs to. */
    postId: z.string().min(1).max(120),
    /** Unique accounts that saw the post. */
    reach: z.number().int().min(0).nullable(),
    /** Total impressions (deprecated by Meta but still surfaced when
     *  available; reach is the more reliable signal). */
    impressions: z.number().int().min(0).nullable(),
    /** Saved-to-collection count — usually the strongest "this
     *  was valuable" signal on IG. */
    saves: z.number().int().min(0).nullable(),
    /** Total interactions: likes + comments + saves + shares. Meta's
     *  rollup metric. */
    totalInteractions: z.number().int().min(0).nullable(),
    /** Reels-only. Null for non-reel posts. */
    videoViews: z.number().int().min(0).nullable(),
    /** When we fetched the snapshot. Captures the drift moment in
     *  time so re-scans can show trend. */
    fetchedAt: z.string(),
});
// ─── Account-level audience signal ───────────────────────────────────
/** Online followers by hour-of-day, 0-23 (UTC). Values are average
 *  follower counts online during that hour over the lifetime window.
 *  Used by the agent to recommend best-time-to-post slots that
 *  intersect when this brand's audience is actually awake. */
export const audienceCurveSchema = z.object({
    /** 24 entries, hour 0 (midnight UTC) through hour 23. */
    onlineFollowersByHour: z.array(z.number().int().min(0)).length(24),
    /** Total followers at fetch time. Surfaces as context in the
     *  digest ("you grew from 1,234 to 1,267 this week"). */
    followersTotal: z.number().int().min(0).nullable(),
    fetchedAt: z.string(),
});
// ─── Agent contract ──────────────────────────────────────────────────
/** A post enriched with insights, fed to the digest agent. */
export const enrichedPostSchema = z.object({
    postId: z.string(),
    permalink: z.string().url(),
    mediaType: z.enum(['IMAGE', 'VIDEO', 'CAROUSEL_ALBUM']),
    mediaProductType: z.string().nullable(),
    caption: z.string().max(2000),
    /** ISO timestamp the post went live. */
    timestamp: z.string(),
    /** Engagement counters from the public Graph response. */
    likeCount: z.number().int().min(0),
    commentsCount: z.number().int().min(0),
    /** Insights from the insights endpoint. May be null when the post
     *  is too fresh (<24h) or when the fetch failed for one post in
     *  the batch (others still go through). */
    insights: postInsightsSchema.nullable(),
});
/** Single-post pick the agent surfaces — overperformer, underperformer,
 *  or boost candidate. Carries the post id for the dashboard link plus
 *  Mag's hypothesis in her voice. */
export const digestPickSchema = z.object({
    postId: z.string(),
    permalink: z.string().url(),
    caption: z.string().max(280),
    /** Bullets quoting the actual metric values mag is looking at —
     *  e.g. "8.2% engagement vs your 2.3% median", "saved 47 times,
     *  4× your usual". Lowercase, mag voice. */
    receipts: z.array(z.string().min(1).max(160)).min(1).max(4),
    /** One paragraph (≤500 chars) explaining what mag thinks happened
     *  + why. Specific to what's in the caption / format / timing. */
    hypothesis: z.string().min(1).max(500),
});
/** Mag's best-time-to-post recommendation. Carries the recommended
 *  hour band in the BRAND'S local timezone (the agent reasons in UTC
 *  via the audience curve, then converts when emitting). */
export const bestTimeWindowSchema = z.object({
    /** Day of week, lowercase short form: mon, tue, wed, thu, fri,
     *  sat, sun. Empty array = "any weekday — your audience is
     *  always-on". */
    dayOfWeek: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).max(7),
    /** Local-tz hour range, 0-23. inclusive on both ends. */
    hourStart: z.number().int().min(0).max(23),
    hourEnd: z.number().int().min(0).max(23),
    /** Why this window: mag's one-sentence rationale. */
    reason: z.string().min(1).max(280),
});
export const metaEngagementDigestOutputSchema = z.object({
    /** One-sentence mag-voice summary that goes on the feed card. */
    summary: z.string().min(1).max(280),
    /** 1-3 candidate windows mag thinks make sense given the audience
     *  curve + when this brand's winners posted. */
    bestTimeToPost: z.array(bestTimeWindowSchema).min(1).max(3),
    /** The week's best performer + hypothesis. Null when there's
     *  literally nothing to compare (e.g. brand just connected). */
    overperformer: digestPickSchema.nullable(),
    /** The week's worst performer + hypothesis. Null when no clear
     *  laggard or sample is too small. */
    underperformer: digestPickSchema.nullable(),
    /** The post mag would put money on if the founder wanted to
     *  boost. Often the same as overperformer but can differ —
     *  e.g. an organic mid-performer with the right targeting
     *  cues for a cold audience. Null when paid wouldn't help
     *  meaningfully. */
    boostCandidate: digestPickSchema.nullable(),
    /** Mag's confidence on the read, 0-10. Lower when the window
     *  has <3 posts or the audience signal is sparse. */
    confidence: z.number().int().min(0).max(10),
});
// ─── Feed card payload ───────────────────────────────────────────────
/** Feed card that lands daily at brand 9am-tz carrying mag's read of
 *  the week's engagement. Lands status='open' so the founder
 *  explicitly acks it (signal for the trust ledger). */
export const metaEngagementDigestCardPayloadSchema = z.object({
    kind: z.literal('meta_engagement_digest'),
    /** ISO date (YYYY-MM-DD in brand tz) the digest is for. Used to
     *  dedup re-runs within the same day. */
    digestDate: z.string(),
    /** Posts the digest is based on. The renderer shows counts; the
     *  agent grounds its picks in this list. */
    postsAnalyzed: z.number().int().min(0),
    /** Days the window covered. Usually 7 but the very first digest
     *  for a freshly connected brand may be shorter. */
    windowDays: z.number().int().min(1).max(30),
    /** Audience curve snapshot at the time of the run. Renderer
     *  shows it as a faint background behind the best-time pill.
     *  Null when the fetch failed (older Meta connections that
     *  hadn't granted insights scope yet). */
    audienceCurve: audienceCurveSchema.nullable(),
    /** The agent's output. */
    read: metaEngagementDigestOutputSchema,
    /** Cost in USD of the LLM call. Surfaces in the card footer
     *  for transparency, same as the history-learn card. */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=meta-engagement.js.map